import clsx from 'clsx'
import { type ChangeEvent, useState } from 'react'
import toast from 'react-hot-toast'

import { FvButton, Icon, SelectField } from '@fv/client-components'
import type { EquipmentType } from '@fv/models/equipment'

import { Loading } from '@/components/Loading'
import { countLoads } from '@/features/quote-request/quoteRequestUtils'
import { useCanUseMultiTruck } from '@/features/quote-request/useCanUseMultiTruck'
import {
  useQuoteFormActions,
  useQuoteFormLoads,
} from '@/features/quote-request/useQuoteFormState'
import { useFavoriteEquipmentTypes } from '@/features/settings/user-preferences/hooks'
import { useQuoteSettings } from '@/hooks/settings'
import type { EquipmentTypeEnum } from '@/types'
import arrayOfLength from '@/utils/arrayOfLength'

import CheckboxField from '../../components/inputs/CheckboxField'
import { Pinline } from '../../components/Pinline'
import { ShipperTooltip } from '../../components/ShipperTooltip'
import TruckSelector from '../../components/shared/TruckSelector'
import { EquipmentConfig } from './EquipmentConfig'

const truckQuantityOptions = arrayOfLength(6).map(String)

type Props = {
  isEditing: boolean
}

export const EquipmentList = ({ isEditing }: Props) => {
  const { removeLoads } = useQuoteFormActions()
  const [isShowingAll, setShowingAll] = useState(false)
  const buttonLabel = isShowingAll ? 'Favorite equipment' : 'More equipment'
  const quoteSettingsQuery = useQuoteSettings('truckload')
  const equipmentOptions = quoteSettingsQuery.data?.equipmentOptions ?? []
  const loads = useQuoteFormLoads()
  const loadCount = countLoads(loads)
  const favoriteTypes = useFavoriteEquipmentTypes()
  const hasNonFavoriteEquipment = Array.from(loads.keys()).some(
    t => !favoriteTypes.includes(t),
  )

  // List always expanded if non-favorite selected to allow form validation
  const filteredOptions = equipmentOptions.filter(
    o =>
      isShowingAll || hasNonFavoriteEquipment || favoriteTypes.includes(o.key),
  )

  if (!equipmentOptions.length || (isEditing && !loadCount)) {
    return (
      <div className="bg-fv-blue-50 px-6 b1350:row-start-3 pt-8 pb-12 overflow-auto border-fv-blue-100 border-r ">
        <Loading />
      </div>
    )
  }

  function toggleShowingAll() {
    const equipmentToRemove: EquipmentType[] = []

    if (isShowingAll) {
      for (const type of loads.keys()) {
        const equipment = equipmentOptions.find(o => o.key === type)
        if (!equipment?.favorite) equipmentToRemove.push(type)
      }
    }

    setShowingAll(!isShowingAll)
    equipmentToRemove.forEach(type => removeLoads(type))
  }

  return (
    <div className="bg-fv-blue-50 b1350:row-start-3 px-6 pt-8 pb-12 overflow-auto border-fv-blue-150 border-r">
      <header className="flex">
        <h6 className="required">Equipment type</h6>
        <h6 className="required ml-auto">Trucks</h6>
      </header>

      <div className="equipment-types">
        {filteredOptions.map(equipmentType => (
          <EquipmentTypeForm
            isEditing={isEditing}
            equipmentType={equipmentType}
            key={equipmentType.key}
          />
        ))}
      </div>

      {!hasNonFavoriteEquipment && (
        <div className="border-fv-blue-400 border-t border-dashed pt-4 fa-width-auto">
          <FvButton onClick={toggleShowingAll} icon="arrows-alt-v">
            {buttonLabel}
          </FvButton>
        </div>
      )}
    </div>
  )
}

type EtFormProps = Props & {
  equipmentType: EquipmentTypeEnum
}
const EquipmentTypeForm = ({ isEditing, equipmentType }: EtFormProps) => {
  const { activateLoad, addLoads, removeLoads } = useQuoteFormActions()
  const quoteSettingsQuery = useQuoteSettings('truckload')
  const loads = useQuoteFormLoads()
  const canUseMultiTruck = useCanUseMultiTruck()
  const equipmentOptions = quoteSettingsQuery.data?.equipmentOptions ?? []

  const equipmentKey = equipmentType.key
  const equipmentName = equipmentType.name
  const equipmentOfType = loads.get(equipmentKey)
  const equipmentCount = equipmentOfType?.length ?? 0
  const isChecked = equipmentCount > 0
  const activeIndex = equipmentOfType?.findIndex(e => e.isActiveInUI) ?? 0
  const loadCount = countLoads(loads)

  return (
    <>
      <div
        className={clsx(
          'quote-equipment-type-group relative ',

          isChecked &&
            'first:mt-[.6125rem] mt-8 mb-9 before:bg-white before:inset-0 before:absolute before:border-fv-gray before:border-t before:border-b before:-mx-6 before:-mt-[1.1rem] before:-mb-[1.2rem] [&>*]:before:absolute [&>*]:before:bg-fv-gray-dark [&>*]:after:bg-fv-gray-dark [&>*]:after:absolute [&>*:nth-child(-n+2)]:before:w-[14px] [&>*:nth-child(-n+2)]:before:h-px [&>*:nth-child(-n+2)]:before:-left-[14px] [&>*:nth-child(-n+2)]:before:top-1/2 after:top-[calc(1rem-1px)] after:absolute after:w-px after:bg-fv-gray-dark after:-left-[14px] after:bottom-3 ',
        )}
        data-cy="equipment-form"
      >
        <div
          className="items-center flex justify-between mb-4 relative"
          title={equipmentName}
        >
          <CheckboxField
            checked={isChecked}
            alternate
            className="checkbox"
            label={equipmentName}
            name={equipmentKey}
            disabled={!canUseMultiTruck && !isChecked && loadCount >= 1}
            labelClassName="truncate"
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              if (e.target.checked) {
                addLoads(equipmentKey)
              } else if (isEditing && loadCount <= 1) {
                toast.error('Must select at least one equipment type.')
              } else {
                removeLoads(equipmentKey)
              }
            }}
          />
          <Pinline dashed className="ml-2" flex />
          <SelectField
            className="form-control form-control--select h-auto px-[.3rem] py-[.15rem] ml-3 w-auto"
            name={`${equipmentName}_quantity`}
            disabled={!canUseMultiTruck}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => {
              const quantity = Number(e.target.value)

              if (quantity > equipmentCount) {
                addLoads(equipmentKey, quantity - equipmentCount)
              }

              if (quantity < equipmentCount) {
                removeLoads(equipmentKey, quantity, equipmentCount - quantity)
              }
            }}
            options={truckQuantityOptions}
            value={String(equipmentCount)}
          />
        </div>

        {equipmentCount > 0 && (
          <div className="flex items-center gap-x-1 mb-3 relative">
            <span className="mr-1">Truck</span>

            {equipmentOfType?.map((e, i) => (
              <TruckSelector
                active={e.isActiveInUI}
                key={e.loadId}
                selectTruck={() => activateLoad(e.type, e.loadId)}
                truckNumber={i + 1}
              />
            ))}

            <Pinline dotted className="mx-1" flex />
            <ShipperTooltip label="Remove this truck">
              <FvButton
                icon="trash"
                iconClass="text-fv-orange-bright"
                onClick={() => {
                  const startIndex = equipmentOfType?.findIndex(
                    e => e.isActiveInUI,
                  )

                  removeLoads(equipmentKey, startIndex, 1)
                }}
              ></FvButton>
            </ShipperTooltip>
          </div>
        )}

        {equipmentOfType?.map(e => (
          <EquipmentConfig
            accessorialOptions={equipmentType.accessorialOptions}
            altEquipmentOptions={equipmentOptions
              .filter(o => o.key !== equipmentKey)
              .map(o => ({
                label: o.name,
                value: o.key,
              }))}
            key={e.loadId}
            load={e}
          />
        ))}
      </div>

      {equipmentCount > 1 && (
        <div className="-mx-6 -mt-5 mb-4">
          <FvButton
            className="bg-white px-6 py-4 relative z-10 border-t-fv-blue border-dotted border w-full border-b-fv-gray border-b-solid text-left"
            icon="truck-moving"
            onClick={() => {
              const nextTruck =
                equipmentOfType?.[activeIndex + 1] ?? equipmentOfType?.[0]

              nextTruck && activateLoad(nextTruck.type, nextTruck.loadId)
            }}
          >
            Next truck
            <Icon icon="arrow-right" className="ml-2" />
          </FvButton>
        </div>
      )}
    </>
  )
}
